import { useEffect, useMemo, useRef, useState } from 'react';
import { closePrice, cryptoCoin, D, fmt, fmtPrice, fmtSigned, fmtUsd, fmtUsdSigned, freeMargin, NETWORKS, pairLabel, pnl, rangeQuery, todayIn, usdPerPoint, type AccountOverview, type CryptoDepositConfig, type CryptoPayment, type DayRange, type EquityPoint, type LedgerEntry, type Position } from '@platform/shared';
import { CustomRangeTab } from '../../components/DateRange';
import { InstrumentIcon } from '../../components/InstrumentIcon';
import { cx, sideLabel, Spinner, UsdtIcon } from '../../components/ui';
import { api } from '../../lib/api';
import { Link, navigate } from '../../lib/router';
import { useLoad, useVersion } from '../../stores/account';
import { useInstruments, useTickers } from '../../stores/market';
import { useMe } from '../../stores/session';
import { useInvestmentTotals } from '../../stores/investments';
import { useManagedTotals } from '../../stores/managed';
import { useLiveAccount, usePositions } from '../../stores/trading';
import { CoinPicker, Dropdown } from './deposit/CoinPicker';
import { ASSET_NAME, CoinIcon, DEFAULT_COINS, networkOptions, ordered } from './deposit/coins';
import { resumable, useFollowPayment } from './deposit/payment';
import { PayDialog } from './deposit/PayDialog';
import { PendingDocumentCard } from './Documents';
import { EquityChart } from './EquityChart';
import { ledgerLabel, TransactionTicket, transactionId } from './TransactionTicket';
import { AIcon, Alert, Card, errMsg, Field, H2, KycBadge, PillButton, PillLink, Segmented, when, type AcctIconName } from './kit';
import { NumInput } from '../../components/NumInput';
import { UsdtChip } from '../../components/UsdtInfo';
import { HelpTip } from '../../components/HelpTip';
import { HELP } from './help';


const signCls = (v: string | D) => (D.from(v).isNeg() ? 'text-down' : D.from(v).isPos() ? 'text-up' : '');

type StepState = 'done' | 'waiting' | 'todo';

function SetupHero({ kyc, funded, traded }: { kyc: string; funded: boolean; traded: boolean }) {
  const strip = useRef<HTMLDivElement>(null);
  // Native scrollbars clash with the black hero, so the strip hides its own and draws a slim track instead.
  const [bar, setBar] = useState({ size: 1, start: 0 });
  // Finished steps stay on the card so clients see their progress; the card goes once all are done.
  const tasks: { state: StepState; title: string; text: string; cta: string; to: string }[] = [
    {
      state: kyc === 'approved' ? 'done' : kyc === 'pending' ? 'waiting' : 'todo',
      title: 'Verify your identity',
      text:
        kyc === 'approved'
          ? 'Your identity is verified.'
          : kyc === 'pending'
            ? 'We are working on verifying your identity.'
            : kyc === 'rejected'
              ? 'One of your documents was rejected. Upload a new one.'
              : kyc === 'partial'
                ? 'Partly verified. Send the documents we asked for to enable withdrawals.'
                : 'Upload your ID, a selfie and a proof of address.',
      cta: kyc === 'rejected' ? 'Resubmit' : kyc === 'partial' ? 'Finish verification' : 'Verify identity',
      to: '/account/kyc',
    },
    {
      state: funded ? 'done' : 'todo',
      title: 'Make your first deposit',
      text: funded ? 'Your account is funded.' : 'Fund your account with USDT and get ready to trade.',
      cta: 'Deposit',
      to: '/account/deposit',
    },
    {
      state: traded ? 'done' : 'todo',
      title: 'Place your first trade',
      text: traded ? 'You placed your first trade.' : 'Open your first position on crypto, forex or indices from the trading screen.',
      cta: 'Start trading',
      to: '/trade',
    },
  ];
  const doneCount = tasks.filter((t) => t.state === 'done').length;
  const current = tasks.findIndex((t) => t.state === 'todo');
  const measure = () => {
    const el = strip.current;
    if (!el || !el.scrollWidth) return;
    setBar({ size: el.clientWidth / el.scrollWidth, start: el.scrollLeft / el.scrollWidth });
  };
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doneCount]);
  // Bring the next step into view, so finished steps don't push it off the strip.
  useEffect(() => {
    const el = strip.current;
    const card = el?.children[current] as HTMLElement | undefined;
    if (el && card && el.scrollWidth > el.clientWidth) el.scrollLeft = card.offsetLeft - (el.firstElementChild as HTMLElement).offsetLeft;
  }, [current]);
  if (doneCount === tasks.length) return null;
  const overflow = bar.size < 0.995;
  const atStart = bar.start <= 0.005;
  const atEnd = bar.start + bar.size >= 0.995;
  const scroll = (dir: number) => strip.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  return (
    <section className="rounded-[24px] bg-hero p-5 text-hero-fg md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="m-0 text-[22px] font-semibold md:text-[26px]">Finish account setup</h2>
          <p className="m-0 mt-3 text-[15px] md:text-[17px]">Complete these tasks to get the most out of your account.</p>
          <div className="mt-4 flex items-center gap-3 text-[14px] text-hero-muted">
            <span className="flex gap-1.5" aria-hidden>
              {tasks.map((t) => (
                <i key={t.title} className={cx('h-1 w-9 rounded-full', t.state === 'done' ? 'bg-hero-fg' : 'bg-hero-line')} />
              ))}
            </span>
            {doneCount} of {tasks.length} completed
          </div>
        </div>
        {overflow && (
          <div className="hidden gap-2 pt-1 md:flex">
            {([['Previous', 'left', -1, atStart], ['Next', 'right', 1, atEnd]] as const).map(([label, icon, dir, off]) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                disabled={off}
                onClick={() => scroll(dir)}
                className="grid size-9 place-items-center rounded-full border border-hero-line text-hero-fg transition-colors hover:bg-hero-card disabled:cursor-default disabled:text-hero-muted disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <AIcon name={icon} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div ref={strip} onScroll={measure} className="no-scrollbar mt-6 flex flex-col gap-3 md:mt-7 md:snap-x md:snap-mandatory md:flex-row md:gap-5 md:overflow-x-auto">
        {tasks.map((t, i) => (
          <div
            key={t.title}
            className={cx('flex shrink-0 snap-start flex-col rounded-[20px] border border-hero-line p-5 md:w-[300px] md:p-6', t.state === 'done' ? 'bg-transparent' : 'bg-hero-card')}
          >
            <span
              className={cx(
                'grid size-11 place-items-center rounded-full',
                t.state === 'done' ? 'bg-[#12301f] text-[#5fd68a]' : i === current ? 'bg-white text-black' : 'border-2 border-white text-white',
              )}
            >
              {t.state === 'done' ? <AIcon name="check" size={22} /> : <b className="text-[17px]">{i + 1}</b>}
            </span>
            <b className={cx('mt-4 text-[17px] md:mt-6', t.state === 'done' && 'text-hero-muted')}>{t.title}</b>
            <span className="mt-1 text-[16px] leading-snug text-hero-muted">{t.text}</span>
            <div className="mt-auto pt-5 md:pt-8">
              {t.state === 'done' ? (
                <span className="flex h-[52px] items-center gap-1.5 text-[16px] font-medium text-[#5fd68a]">
                  <AIcon name="check" size={18} /> Completed
                </span>
              ) : t.state === 'waiting' ? (
                <span className="inline-block rounded-md bg-[#1d2b6b] px-2.5 py-1 text-[15px] font-medium text-[#9db0ff]">Verifying</span>
              ) : (
                <Link
                  to={t.to}
                  className={cx(
                    'flex h-[52px] items-center justify-center rounded-full text-[17px] font-semibold no-underline',
                    i === current ? 'bg-white text-black' : 'border border-hero-line text-hero-fg hover:bg-hero-card',
                  )}
                >
                  {t.cta}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
      {overflow && (
        <div aria-hidden className="relative mt-5 hidden h-1 overflow-hidden rounded-full bg-hero-line md:block">
          <div
            className="absolute inset-y-0 rounded-full bg-hero-fg transition-[left] duration-150"
            style={{ width: `${bar.size * 100}%`, left: `${bar.start * 100}%` }}
          />
        </div>
      )}
    </section>
  );
}

const RANGES = [
  { id: '7', label: '7D' },
  { id: '30', label: '30D' },
  { id: '90', label: '90D' },
  { id: '0', label: 'All' },
] as const;
type Range = (typeof RANGES)[number]['id'];

function savedRange(): Range {
  try {
    const r = localStorage.getItem('equityRange');
    if (RANGES.some((x) => x.id === r)) return r as Range;
  } catch {
    /* ignore */
  }
  return '30';
}

function EquityCard({ accountId, liveEquity }: { accountId: string; liveEquity: string | null }) {
  const [range, setRange] = useState<Range>(savedRange);
  // A custom range of UTC snapshot days; not saved, the next visit opens on the saved preset.
  const [custom, setCustom] = useState<DayRange | null>(null);
  const { data, loading } = useLoad<EquityPoint[]>(`/api/account/equity?accountId=${accountId}&${custom ? rangeQuery(custom, 'utc') : `days=${range}`}`);
  const toToday = !custom || custom.to >= todayIn('utc');
  const points = useMemo(() => {
    const p = [...(data ?? [])];
    if (liveEquity && toToday) {
      const today = new Date().toISOString().slice(0, 10);
      const live = { day: today, equity: liveEquity, balance: liveEquity };
      if (p.length && p[p.length - 1]!.day === today) p[p.length - 1] = live;
      else p.push(live);
    }
    return p;
  }, [data, liveEquity, toToday]);
  const first = points[0]?.equity;
  const last = points[points.length - 1]?.equity;
  const change = first && last ? D.from(last).sub(first) : null;
  const pct = change && first && !D.from(first).isZero() ? change.mul(100).div(first) : null;
  return (
    <Card className="flex min-w-0 flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <H2>Equity</H2>
        <div className="flex items-center gap-1.5">
          <Segmented
            label="Equity range"
            size="sm"
            options={RANGES.map((r) => ({ id: r.id, label: r.label }))}
            value={custom ? ('' as Range) : range}
            onChange={(r) => {
              setRange(r);
              setCustom(null);
              try {
                localStorage.setItem('equityRange', r);
              } catch {
                /* ignore */
              }
            }}
            className="[&>button]:py-1.5 [&>button]:text-[14px]"
          />
          <CustomRangeTab zone="utc" value={custom} onApply={setCustom} className="h-9 min-w-9 rounded-full px-2.5 text-[14px] font-semibold" activeClassName="bg-pill-on text-fg" idleClassName="bg-track text-muted hover:text-fg" />
        </div>
      </div>
      <div className={cx('num mt-2 h-5 text-[15px]', change && signCls(change))}>
        {change && points.length > 1 ? `${fmtUsdSigned(change)}${pct ? ` (${fmtSigned(pct, 2)}%)` : ''}` : ''}
      </div>
      <div className="relative mt-3">
        {points.length > 1 ? (
          <EquityChart points={points} up={!change?.isNeg()} />
        ) : (
          <div className="grid h-[260px] place-items-center rounded-[16px] bg-track px-6 text-center text-[15px] text-muted md:h-[300px]">
            {loading ? <Spinner /> : custom ? 'No daily snapshots in this period.' : 'Your equity curve starts tomorrow: we save one snapshot per day at 00:00 UTC.'}
          </div>
        )}
      </div>
      <p className="m-0 mt-4 text-[14px] text-muted">One snapshot per day at 00:00 UTC.{toToday ? ' The last point is live.' : ''}</p>
    </Card>
  );
}

function PositionsTable({ positions }: { positions: Position[] }) {
  const tickers = useTickers();
  const instruments = useInstruments();
  const bySym = useMemo(() => Object.fromEntries(instruments.map((i) => [i.symbol, i])), [instruments]);
  return (
    <table className="w-full border-collapse text-[15px] md:text-[17px]">
      <thead>
        <tr className="border-b border-line">
          <th className="px-2 py-4 text-left font-semibold md:px-6">Asset</th>
          <th className="px-3 py-4 text-left font-semibold max-md:hidden">Side</th>
          <th className="px-3 py-4 text-left font-semibold max-md:hidden">Mark price</th>
          <th className="px-2 py-4 text-right font-semibold md:px-3 md:text-left">uPnL</th>
          <th className="px-3 py-4 text-left font-semibold max-md:hidden">ROE</th>
          <th className="max-md:hidden" />
        </tr>
      </thead>
      <tbody>
        {positions.map((p) => {
          const inst = bySym[p.symbol];
          const t = tickers[p.symbol];
          const dp = inst?.priceDecimals ?? 2;
          const ccy = inst?.priceCcy ?? 'USD';
          const u = t ? pnl(p.side, p.entryPrice, closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) }), p.qty, usdPerPoint(inst?.contractSize ?? '1', t.fx)).add(p.swap) : null;
          const roe = u ? u.mul(100).div(p.margin) : null;
          const side = sideLabel(p.side);
          return (
            <tr key={p.id} className="border-b border-line">
              <td className="px-2 py-4 md:px-6 md:py-5">
                <div className="flex items-center gap-3 md:gap-4">
                  <InstrumentIcon symbol={p.symbol} size={44} />
                  <div className="min-w-0">
                    <div className="truncate">{inst?.name ?? p.symbol}</div>
                    <div className="text-[14px] text-muted md:text-[16px]">{pairLabel(p.symbol)}</div>
                  </div>
                </div>
              </td>
              <td className="px-3 max-md:hidden">
                <span className={p.side === 'long' ? 'text-up' : 'text-down'}>{side}</span> <span className="rounded-md bg-track px-1.5 text-[13px] text-muted">{p.leverage}x</span>
              </td>
              <td className="num px-3 max-md:hidden">{t ? fmtPrice(t.mark, dp, ccy) : '—'}</td>
              <td className={cx('num px-2 text-right md:px-3 md:text-left', u && signCls(u))}>
                {u ? fmtUsdSigned(u) : '—'}
                <div className={cx('text-[13px] md:hidden', p.side === 'long' ? 'text-up' : 'text-down')}>
                  {side} {p.leverage}x
                </div>
              </td>
              <td className={cx('num px-3 max-md:hidden', roe && signCls(roe))}>{roe ? `${fmtSigned(roe, 2)}%` : '—'}</td>
              <td className="px-6 text-right max-md:hidden">
                <Link to={`/trade/${p.symbol}`} className="inline-flex h-[42px] items-center rounded-full bg-pill px-5 text-[15px] font-semibold whitespace-nowrap text-fg no-underline">
                  View details
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const QUICK: [number, string][] = [
  [100, '100'],
  [500, '500'],
  [1000, '1,000'],
  [5000, '5,000'],
];
/** Deposit quick amounts start at the crypto minimum ($250). */
const QUICK_DEPOSIT: [number, string][] = [
  [250, '250'],
  [500, '500'],
  [1000, '1,000'],
  [5000, '5,000'],
];
const OFF: CryptoDepositConfig = { enabled: false, minUsd: '250', coins: [] };

function MoveFunds({ kyc, liveBalance, liveAvailable }: { kyc: string; liveBalance: string; liveAvailable: string }) {
  const [tab, setTab] = useState<'deposit' | 'withdraw'>('deposit');
  const [network, setNetwork] = useState(NETWORKS[0]!.id);
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const locked = tab === 'withdraw' && kyc !== 'approved';

  // Deposit: the coins selected in NOWPayments, as currency (USDT, ETH, BTC, XRP …) + network.
  const { data } = useLoad<CryptoDepositConfig>('/api/deposits/crypto/config');
  const cfg = data ?? OFF;
  const coins = ordered(cfg.coins.length ? cfg.coins : DEFAULT_COINS);
  const [coin, setCoin] = useState('');
  // Until the client picks, the first in page order (USDT on ERC20).
  const chosen = coins.includes(coin) ? coin : (coins[0] ?? 'usdterc20');
  const k = cryptoCoin(chosen);
  const min = Number(cfg.minUsd);

  // The payment opens in a pop-up on the dashboard. Closing it keeps the payment open: the card then
  // offers to show it again, also after a reload (until it is paid, cancelled or runs out).
  const v = useVersion('deposit');
  const { data: payments, reload } = useLoad<CryptoPayment[]>('/api/deposits/crypto', [v]);
  const [pay, setPay] = useState<CryptoPayment | null>(null);
  const [showPay, setShowPay] = useState(false);
  const [left, setLeft] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pay || !payments) return;
    const open = payments.find((p) => !left.includes(p.id) && resumable(p));
    if (open) setPay(open);
  }, [payments, pay, left]);
  useFollowPayment(pay, setPay);
  const waiting = pay && !showPay && resumable(pay) ? pay : null;
  const done = () => {
    if (pay) setLeft((l) => [...l, pay.id]);
    setPay(null);
    setShowPay(false);
    void reload();
  };

  const go = async () => {
    if (tab === 'deposit') {
      const a = Number(amount) || 0;
      if (a < min) return setErr(a ? `The minimum crypto deposit is $${fmt(cfg.minUsd, 0)}.` : `Enter the amount you want to deposit (at least $${fmt(cfg.minUsd, 0)}).`);
      if (!cfg.enabled) return setErr('Crypto payments are being switched on. Try again in a little while.');
      setBusy(true);
      setErr(null);
      try {
        const p = await api.post<CryptoPayment>('/api/deposits/crypto', { amount, coin: chosen });
        if (pay) setLeft((l) => [...l, pay.id]);
        setPay(p);
        setShowPay(true);
        void reload();
      } catch (x) {
        setErr(errMsg(x));
      } finally {
        setBusy(false);
      }
      return;
    }
    const qs = new URLSearchParams({ network });
    if (amount) qs.set('amount', amount);
    navigate(`/account/withdraw?${qs}`);
  };
  return (
    <Card>
      <H2>Move funds</H2>
      <Segmented
        label="Move funds"
        size="lg"
        className="mt-7"
        options={[
          { id: 'deposit', label: 'Deposit' },
          { id: 'withdraw', label: 'Withdraw' },
        ]}
        value={tab}
        onChange={(t) => {
          setTab(t);
          setErr(null);
        }}
      />
      {locked && (
        <Alert className="mt-7">
          {kyc === 'pending'
            ? 'We are verifying your identity and will be in touch shortly. Withdrawals unlock as soon as compliance approves your documents.'
            : kyc === 'partial'
              ? 'Your identity is partly verified. Send the documents we asked for to enable withdrawals.'
              : 'Verify your identity to enable withdrawals. You can keep trading and depositing in the meantime.'}
        </Alert>
      )}
      {tab === 'deposit' && waiting && (
        <div className="mt-7 flex items-center justify-between gap-3 rounded-[14px] bg-warn-bg px-4 py-3 text-[15px] font-semibold text-warn">
          <span className="min-w-0">
            Payment waiting · ${fmt(waiting.amountUsd, 0)} {cryptoCoin(waiting.payCurrency).symbol}
          </span>
          <button type="button" onClick={() => setShowPay(true)} className="shrink-0 rounded-full bg-card px-4 py-1.5 text-[14px] font-semibold text-fg">
            Show
          </button>
        </div>
      )}
      {tab === 'deposit' ? (
        <CoinPicker coins={coins} value={chosen} onChange={setCoin} className="mt-7" />
      ) : (
        <div className="mt-7">
          <Dropdown label="Network" value={network} onChange={(v) => setNetwork(v as typeof network)} options={networkOptions()} />
        </div>
      )}
      <Field label="Amount" htmlFor="mf-amount" className="mt-7" error={err}>
        <div className="field flex !h-[64px] items-center gap-2 md:!h-[72px]">
          <span className="text-[20px] text-muted">$</span>
          <NumInput
            id="mf-amount"
            placeholder="0"
            value={amount}
            onValue={(v) => {
              setAmount(v.replace(/[^\d.]/g, ''));
              setErr(null);
            }}
            className="num min-w-0 flex-1 bg-transparent text-[20px] text-fg outline-none placeholder:text-muted"
          />
          <span className="text-[16px] text-muted">USDT</span>
        </div>
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          {(tab === 'deposit' ? QUICK_DEPOSIT : QUICK).map(([v, l]) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setAmount(String(v));
                setErr(null);
              }}
              className="num h-[42px] rounded-full bg-pill text-[15px] font-semibold md:text-[16px]"
            >
              {l}
            </button>
          ))}
        </div>
      </Field>
      <div className="mt-7 rounded-[20px] border border-line px-5">
        <div className="flex items-center gap-4 border-b border-line py-5">
          {tab === 'deposit' ? <CoinIcon code={chosen} size={44} /> : <UsdtIcon size={44} />}
          <div className="min-w-0 flex-1">
            {tab === 'deposit' ? (
              <>
                <div className="text-[17px]">
                  {ASSET_NAME[k.symbol] ?? k.symbol} · {k.symbol}
                  {k.network && k.network !== ASSET_NAME[k.symbol] ? ` on ${k.network}` : ''}
                </div>
                <div className="text-[15px] text-muted">Credited in USDT, the amount you enter</div>
              </>
            ) : (
              <>
                <div className="text-[17px]">USDT</div>
                <div className="text-[15px] text-muted">Tether · credited 1 USDT = $1</div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 py-5">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-fg text-bg">
            <AIcon name="wallet" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[17px]">{tab === 'deposit' ? 'To live account' : 'From live account'}</div>
            <div className="num text-[15px] text-muted">{tab === 'deposit' ? fmtUsd(liveBalance) : `${fmtUsd(liveAvailable)} free`}</div>
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <PillButton kind="secondary" size="lg" onClick={() => navigate(tab === 'deposit' ? '/account/deposit' : '/account/withdraw')}>
          Details <AIcon name="right" />
        </PillButton>
        <PillButton size="lg" onClick={() => void go()} busy={busy} disabled={locked}>
          {tab === 'deposit' ? 'Get address' : 'Review'}
        </PillButton>
      </div>
      {pay && showPay && <PayDialog p={pay} onClose={() => setShowPay(false)} onReset={done} />}
    </Card>
  );
}

function VerificationCard({ kyc }: { kyc: string }) {
  const steps: [string, 'done' | 'current' | 'bad' | 'todo'][] = [
    ['Documents submitted', kyc === 'none' ? 'current' : 'done'],
    ['Under review', kyc === 'pending' ? 'current' : kyc === 'none' ? 'todo' : 'done'],
    [kyc === 'rejected' ? 'Rejected' : kyc === 'partial' ? 'Partly verified' : 'Approved', kyc === 'approved' ? 'done' : kyc === 'rejected' ? 'bad' : kyc === 'partial' ? 'current' : 'todo'],
  ];
  const text = {
    none: 'Upload an ID, a selfie and a proof of address. You can trade now. Withdrawals unlock after approval.',
    pending: 'We are verifying your identity and will be in touch shortly. Compliance usually replies within 24 hours.',
    approved: "Your identity is verified. Welcome! Deposits, trading, withdrawals and Yield are all available. We're excited to be part of your trading journey.",
    rejected: 'One of your documents was rejected. Upload a new one to continue.',
    partial: 'Your identity is partly verified. Deposits and trading are open; send the documents we asked for to enable withdrawals.',
  }[kyc as 'none'];
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <H2>Verification</H2>
        <KycBadge status={kyc as 'none'} />
      </div>
      <ol className="m-0 mt-6 flex list-none flex-col gap-1 p-0">
        {steps.map(([l, s]) => (
          <li key={l} className="flex items-center gap-3.5 py-2 text-[17px]">
            <span
              className={cx(
                'grid size-8 place-items-center rounded-full',
                s === 'done' ? 'bg-up-bg text-up' : s === 'bad' ? 'bg-down-bg text-down' : s === 'current' ? 'bg-accent-bg text-accent-fg' : 'bg-track text-muted',
              )}
            >
              <AIcon name={s === 'bad' ? 'x' : s === 'current' ? 'clock' : 'check'} size={15} />
            </span>
            <span className={s === 'todo' ? 'text-muted' : ''}>{l}</span>
          </li>
        ))}
      </ol>
      <p className="m-0 mt-4 text-[16px] text-muted">{text}</p>
      <PillLink to="/account/kyc" kind={kyc === 'none' || kyc === 'rejected' || kyc === 'partial' ? 'primary' : 'secondary'} className="mt-6 w-full">
        {kyc === 'none' ? 'Start verification' : kyc === 'rejected' ? 'Resubmit documents' : kyc === 'partial' ? 'Send documents' : kyc === 'pending' ? 'View submission' : 'View details'}
      </PillLink>
    </Card>
  );
}

export function Dashboard() {
  const me = useMe()!;
  const acc = useLiveAccount();
  const positions = usePositions();
  // Money a dealer manages for the client and money in Smart Portfolios count in Total equity and Balance
  // (margin figures stay the account's own).
  const managed = useManagedTotals();
  const smart = useInvestmentTotals();
  const subs = [managed, smart].filter((t) => t !== null);
  const balanceV = useVersion('balance');
  const live = me.accounts.find((a) => a.kind === 'live')!;
  const accountId = acc?.id ?? live.id;
  const { data: ov } = useLoad<AccountOverview>(`/api/account/overview?accountId=${accountId}`, [balanceV, acc?.balance]);
  // The recent transaction whose ticket is open.
  const [ticket, setTicket] = useState<LedgerEntry | null>(null);
  const [rp, setRp] = useState<'today' | 'd7' | 'd30'>('today');
  const realized = ov?.realized[rp];
  const funded = D.from(live.balance).add(live.credit).isPos() || !!ov?.recent.some((r) => r.type === 'deposit');

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)] 2xl:gap-14">
      <div className="flex min-w-0 flex-col gap-8 xl:gap-11">
        <PendingDocumentCard />
        {ov && <SetupHero kyc={me.kycStatus} funded={funded} traded={ov.hasTraded} />}

        <div className="grid grid-cols-[minmax(0,1fr)] gap-8 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <Card className="flex flex-col">
            <div className="flex items-center justify-between gap-3">
              <H2>Portfolio</H2>
              <span className="rounded-md bg-track px-2.5 py-1 text-[13px] font-medium text-muted">{acc?.kind === 'demo' ? 'Demo account' : 'Live account'}</span>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[16px] md:text-[17px]">
              Total equity <HelpTip icon="info" help={HELP.equity} /> <UsdtChip />
            </div>
            <div className="num mt-1 text-[28px] font-medium md:text-[32px]">
              {acc ? fmtUsd(subs.reduce((v, t) => v.add(t.equity), D.from(acc.equity))) : '—'}
            </div>
            <dl className="m-0 mt-6 flex flex-col gap-4 text-[16px] md:text-[17px]">
              {(
                [
                  subs.length && acc
                    ? ([
                        'Balance',
                        subs.reduce((v, t) => v.add(t.balance), D.from(acc.balance)).toString(),
                        false,
                        {
                          ...HELP.balance,
                          text: `${HELP.balance.text} Includes ${[managed && `${fmtUsd(managed.balance)} on your managed account`, smart && `${fmtUsd(smart.balance)} in your Smart Portfolios`].filter(Boolean).join(' and ')}.`,
                        },
                      ] as const)
                    : (['Balance', acc?.balance, false, HELP.balance] as const),
                  ...(acc && D.from(acc.credit).isPos() ? ([['Credit', acc.credit, false, HELP.credit]] as const) : []),
                  ['Used margin', acc?.marginUsed, false, HELP.usedMargin],
                  ['Free margin', acc && freeMargin(acc.equity, acc.marginUsed, acc.orderHold).toString(), false, HELP.freeMargin],
                  ['Unrealized PnL', acc?.unrealizedPnl, true, HELP.unrealized],
                ] as const
              ).map(([k, v, signed, help]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="flex items-center gap-2">
                    {k} <HelpTip icon="info" help={help} />
                  </dt>
                  <dd className={cx('num m-0', signed && v && signCls(v))}>{v === undefined ? '—' : signed ? fmtUsdSigned(v) : fmtUsd(v)}</dd>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <dt className="flex flex-wrap items-center gap-2">
                  Realized PnL <HelpTip icon="info" help={HELP.realized} />
                  <Segmented
                    label="Realized PnL period"
                    size="sm"
                    options={[
                      { id: 'today', label: 'Today' },
                      { id: 'd7', label: '7D' },
                      { id: 'd30', label: '30D' },
                    ]}
                    value={rp}
                    onChange={setRp}
                    className="p-0.5"
                  />
                </dt>
                <dd className={cx('num m-0 text-right', realized && signCls(realized.pnl))}>
                  {realized ? fmtUsdSigned(realized.pnl) : '—'}
                </dd>
              </div>
            </dl>
            <div className="mt-auto pt-8">
              <PillLink to="/portfolio" kind="secondary">
                View portfolio
              </PillLink>
            </div>
          </Card>
          <EquityCard accountId={accountId} liveEquity={acc?.equity ?? null} />
        </div>

        {!me.totpEnabled && (
          <section className="flex flex-wrap items-center gap-6 rounded-[24px] border border-line bg-card p-5 md:p-8">
            <svg width="104" height="64" viewBox="0 0 104 64" fill="none" aria-hidden="true" className="shrink-0">
              <rect x="4" y="22" width="40" height="38" rx="6" stroke="var(--text)" strokeWidth="2.4" />
              <path d="M14 22V14a10 10 0 0 1 20 0v8" stroke="var(--text)" strokeWidth="2.4" />
              <rect x="56" y="4" width="34" height="56" rx="7" stroke="var(--text)" strokeWidth="2.4" fill="var(--card)" />
              <text x="73" y="37" textAnchor="middle" fontFamily="Geist Mono, monospace" fontSize="11" fontWeight="600" fill="var(--text)">
                507
              </text>
              <circle cx="24" cy="41" r="5" fill="var(--text)" />
            </svg>
            <div className="min-w-[220px] flex-1">
              <h3 className="m-0 text-[20px] font-semibold md:text-[22px]">Protect your account with an authenticator app</h3>
              <p className="m-0 mt-1.5 text-[16px] text-muted md:text-[17px]">Use a 6-digit code from your phone each time you log in.</p>
            </div>
            <PillLink to="/account/security">Set up 2FA</PillLink>
          </section>
        )}

        <section className="flex min-w-0 flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Open positions</h2>
            {positions.length > 0 && (
              <Link to="/portfolio" className="text-[15px] font-semibold text-fg">
                View all {positions.length} →
              </Link>
            )}
          </div>
          {positions.length ? (
            <PositionsTable positions={positions.slice(0, 5)} />
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-[20px] border border-line px-6 py-6 text-[16px] text-muted">
              No open positions on your {acc?.kind ?? 'live'} account.
              <PillLink to="/trade" kind="secondary" size="sm">
                Open trading screen
              </PillLink>
            </div>
          )}
        </section>

        <section className="flex min-w-0 flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Recent transactions</h2>
            <Link to="/wallet" className="text-[15px] font-semibold text-fg">
              Full history →
            </Link>
          </div>
          {ov && !ov.recent.length && <div className="rounded-[20px] border border-line px-6 py-6 text-[16px] text-muted">No transactions yet.</div>}
          {!!ov?.recent.length && (
            <table className="w-full border-collapse text-[15px] md:text-[17px]">
              <thead>
                <tr className="border-b border-line">
                  <th className="px-2 py-4 text-left font-semibold md:px-6">Type</th>
                  <th className="px-3 py-4 text-left font-semibold max-md:hidden">Transaction ID</th>
                  <th className="px-3 py-4 text-left font-semibold max-md:hidden">Time</th>
                  <th className="px-2 py-4 text-right font-semibold md:px-6">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ov.recent.map((r) => {
                  const { label, icon } = ledgerLabel(r);
                  return (
                    <tr key={r.id} className="group relative cursor-pointer border-b border-line hover:bg-track">
                      <td className="px-2 py-4 md:px-6">
                        {/* The button covers the whole row (after:inset-0), so any part of it opens the ticket. */}
                        <button type="button" onClick={() => setTicket(r)} aria-label={`Open the ticket for ${label} ${fmtUsdSigned(r.amount)}`} className="flex items-center gap-3 text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-[12px] focus-visible:after:ring-2 focus-visible:after:ring-accent md:gap-4">
                          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-track group-hover:bg-pill">
                            <AIcon name={icon} size={18} />
                          </span>
                          <span className="min-w-0">
                            {label}
                            <span className="num block text-[13px] text-muted md:hidden">{when(r.createdAt)}</span>
                          </span>
                        </button>
                      </td>
                      <td className="num max-w-[260px] truncate px-3 text-muted max-md:hidden">{transactionId(r)}</td>
                      <td className="num px-3 text-[15px] text-muted max-md:hidden">{when(r.createdAt)}</td>
                      <td className={cx('num px-2 text-right md:px-6', signCls(r.amount))}>
                        <span className="inline-flex items-center gap-1.5">
                          {fmtUsdSigned(r.amount)}
                          <AIcon name="right" size={14} className="text-muted group-hover:text-fg max-md:hidden" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {ticket && <TransactionTicket e={ticket} onClose={() => setTicket(null)} />}
        </section>
      </div>

      <div className="flex min-w-0 flex-col gap-8 xl:gap-11">
        <MoveFunds kyc={me.kycStatus} liveBalance={live.balance} liveAvailable={live.withdrawable} />
        <VerificationCard kyc={me.kycStatus} />
        <section className="flex flex-col gap-4">
          <H2>Quick actions</H2>
          <div className="grid grid-cols-2 gap-4">
            {(
              [
                ['/account/deposit', 'down', 'bg-accent text-white', 'Deposit', 'USDT · 3 networks'],
                ['/account/withdraw', 'up', 'bg-fg text-bg', 'Withdraw', `${fmtUsd(live.withdrawable)} free`],
                ['/trade', 'chart', 'bg-up text-white', 'Trade', 'Open trading screen'],
                ['/account/support', 'help', 'bg-pill text-fg', 'Contact support', 'We reply within 24h'],
              ] as const
            ).map(([to, icon, cls, title, sub]) => (
              <Link key={to} to={to} className="flex flex-col gap-5 rounded-[20px] border border-line bg-card p-5 text-fg no-underline hover:border-line-strong">
                <span className={cx('grid size-12 place-items-center rounded-full', cls)}>
                  <AIcon name={icon} size={22} />
                </span>
                <span>
                  <b className="block text-[17px] md:text-[18px]">{title}</b>
                  <span className="num text-[14px] text-muted md:text-[15px]">{sub}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
